import { ref, provide, inject } from 'vue';
import { getOtsResultsByJobId } from '../api/insightsApi';
import { llmInsight } from '../types/insight';
import DDLogs from '../DDLogs';
import { getisFinishedLoadingCashflow } from '../api/cashflowApi';

interface UseInsights {
  insights: llmInsight[];
  isPolling: boolean;
  startPollingByJobId: (
    jobId: string,
    maxAttempts: number,
    interval: number,
    onSuccess: () => void,
    handleError: () => void
  ) => void;
  populateInsights: (jobId: string) => Promise<llmInsight[]>;
  updateIsPolling: (value: boolean) => void;
}

const insights = ref<llmInsight[]>([]);
const isPolling = ref(false);

const updateIsPolling = (value: boolean) => {
  isPolling.value = value;
};

const startPollingByJobId = (
  jobId: string,
  maxAttempts = 50,
  interval = 3000,
  onSuccess: () => void = () => null,
  handleError: () => void = () => null,
) => {
  let attempts = 0;

  isPolling.value = true;

  const checkOtsResults = async () => {
    attempts++;

    try {
      const isFinishedLoading = await getisFinishedLoadingCashflow();
      console.log('isFinishedLoading:', isFinishedLoading, 'attempts:', attempts);

      if (!isFinishedLoading) {
        if (attempts >= maxAttempts) {
          stopPolling();
          handleError();
        }
        return;
      }

      const data = await getOtsResultsByJobId(jobId);
      if (data && data.length > 0) {
        insights.value = data;
        stopPolling();
        onSuccess();
      } else if (attempts >= maxAttempts) {
        stopPolling();
        handleError();
      }
    } catch (error) {
      DDLogs.error('Error polling for insights form server:', error);
      if (attempts >= maxAttempts) {
        stopPolling();
        handleError();
      }
    }
  };

  const stopPolling = () => {
    isPolling.value = false;
    clearInterval(polling);
  };

  const polling = setInterval(checkOtsResults, interval);
  checkOtsResults();
};

const populateInsights = async (jobId: string) => {
  const data = await getOtsResultsByJobId(jobId);
  insights.value = data;
  return data;
};

export const useInsightsService = () => {
  provide('insights', insights);
  provide('isPolling', isPolling);
  provide('populateInsights', populateInsights);
  provide('updateIsPolling', updateIsPolling);
  provide('startPollingByJobId', startPollingByJobId);
};

export const useInsights = (): UseInsights => ({
  insights: inject('insights')!,
  isPolling: inject('isPolling')!,
  populateInsights: inject('populateInsights')!,
  updateIsPolling: inject('updateIsPolling')!,
  startPollingByJobId: inject('startPollingByJobId')!,
});
